import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { Adder, AdderPricingModel } from '@/services/addersService';

// Sentinel value used to mean "create a new category" inside the Select.
// Real categories are user-typed strings, so this prefix is safe from collision.
const CREATE_NEW_CATEGORY = '__create_new_category__';
const NO_CATEGORY = '__no_category__';

interface AdderFormValues {
  name: string;
  category: string | null;
  pricing_model: AdderPricingModel;
  unit_price: number | null;
  unit_label: string | null;
  is_active: boolean;
}

interface AdderFormProps {
  open: boolean;
  adder: Adder | null;
  existingCategories: string[];
  isSaving: boolean;
  onCancel: () => void;
  onSave: (values: AdderFormValues) => Promise<void> | void;
}

const PRICING_MODEL_OPTIONS: {
  value: AdderPricingModel;
  label: string;
  hint: string;
}[] = [
  {
    value: 'fixed',
    label: 'One-time charge',
    hint: 'A flat dollar amount applied once.',
  },
  {
    value: 'per_unit',
    label: 'Price per unit',
    hint: 'Multiplied by a quantity (e.g., per panel, per outlet).',
  },
  {
    value: 'per_watt',
    label: 'Price per watt',
    hint: 'Multiplied by the project system size in watts.',
  },
  {
    value: 'per_foot',
    label: 'Price per foot',
    hint: 'Multiplied by feet (e.g., trenching, conduit).',
  },
  {
    value: 'quote',
    label: 'Quote required',
    hint: 'No fixed price; the team will quote it on the project.',
  },
];

const EMPTY_VALUES: AdderFormValues = {
  name: '',
  category: '',
  pricing_model: 'fixed',
  unit_price: null,
  unit_label: '',
  is_active: true,
};

export function AdderForm({
  open,
  adder,
  existingCategories,
  isSaving,
  onCancel,
  onSave,
}: AdderFormProps) {
  const [values, setValues] = useState<AdderFormValues>(EMPTY_VALUES);
  const [error, setError] = useState<string | null>(null);
  // When true, the category field swaps from a Select to an inline text input
  // so the user can type a brand-new category name.
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  // Reset form whenever the dialog opens (for edit or fresh add).
  useEffect(() => {
    if (!open) return;
    if (adder) {
      setValues({
        name: adder.name,
        category: adder.category ?? '',
        pricing_model: adder.pricing_model,
        unit_price: adder.unit_price,
        unit_label: adder.unit_label ?? '',
        is_active: adder.is_active,
      });
      // If the editing adder's category isn't in the catalog list, we must
      // be in "create" mode so the value still appears in the form.
      const isOrphan =
        !!adder.category && !existingCategories.includes(adder.category);
      setIsCreatingCategory(isOrphan);
    } else {
      setValues(EMPTY_VALUES);
      setIsCreatingCategory(false);
    }
    setError(null);
  }, [open, adder, existingCategories]);

  const showUnitPrice = values.pricing_model !== 'quote';
  const showUnitLabel = values.pricing_model === 'per_unit';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = values.name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }

    if (showUnitPrice) {
      if (values.unit_price == null || Number.isNaN(values.unit_price)) {
        setError('Unit price is required for this pricing model.');
        return;
      }
      if (values.unit_price < 0) {
        setError('Unit price must be zero or greater.');
        return;
      }
    }

    setError(null);

    await onSave({
      name: trimmedName,
      category: values.category?.trim() || null,
      pricing_model: values.pricing_model,
      unit_price: showUnitPrice ? values.unit_price : null,
      unit_label: showUnitLabel ? values.unit_label?.trim() || null : null,
      is_active: values.is_active,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{adder ? 'Edit adder' : 'Add adder'}</DialogTitle>
          <DialogDescription>
            Catalog adders are reusable across projects. Pricing model controls
            how cost is computed when the adder is selected on a project.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="adder-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="adder-name"
              value={values.name}
              onChange={(e) =>
                setValues((v) => ({ ...v, name: e.target.value }))
              }
              placeholder="e.g., Main Panel Upgrade"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adder-category">Category</Label>
            {isCreatingCategory ? (
              <div className="flex items-center gap-2">
                <Input
                  id="adder-category"
                  value={values.category ?? ''}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, category: e.target.value }))
                  }
                  placeholder="e.g., Electrical"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsCreatingCategory(false);
                    setValues((v) => ({ ...v, category: '' }));
                  }}
                  aria-label="Cancel new category"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Select
                value={values.category ? values.category : NO_CATEGORY}
                onValueChange={(v) => {
                  if (v === CREATE_NEW_CATEGORY) {
                    setIsCreatingCategory(true);
                    setValues((prev) => ({ ...prev, category: '' }));
                    return;
                  }
                  if (v === NO_CATEGORY) {
                    setValues((prev) => ({ ...prev, category: '' }));
                    return;
                  }
                  setValues((prev) => ({ ...prev, category: v }));
                }}
              >
                <SelectTrigger id="adder-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>
                    <span className="text-muted-foreground">No category</span>
                  </SelectItem>
                  {existingCategories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_NEW_CATEGORY}>
                    <span className="flex items-center text-primary">
                      <Plus className="h-3 w-3 mr-1" />
                      Add new category
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-muted-foreground">
              {isCreatingCategory
                ? 'Type a new category name. It will be saved with this adder.'
                : 'Pick an existing category or add a new one. Used to group adders in the project picker.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adder-pricing-model">
              Pricing model <span className="text-destructive">*</span>
            </Label>
            <Select
              value={values.pricing_model}
              onValueChange={(v) =>
                setValues((prev) => ({
                  ...prev,
                  pricing_model: v as AdderPricingModel,
                  // Reset model-specific fields when switching
                  unit_price: v === 'quote' ? null : prev.unit_price,
                  unit_label: v === 'per_unit' ? prev.unit_label : '',
                }))
              }
            >
              <SelectTrigger id="adder-pricing-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRICING_MODEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {
                PRICING_MODEL_OPTIONS.find(
                  (o) => o.value === values.pricing_model,
                )?.hint
              }
            </p>
          </div>

          {showUnitPrice && (
            <div className="space-y-2">
              <Label htmlFor="adder-unit-price">
                Unit price <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="adder-unit-price"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  className="pl-7"
                  value={values.unit_price ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setValues((v) => ({
                      ...v,
                      unit_price: raw === '' ? null : Number(raw),
                    }));
                  }}
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          {showUnitLabel && (
            <div className="space-y-2">
              <Label htmlFor="adder-unit-label">Unit label</Label>
              <Input
                id="adder-unit-label"
                value={values.unit_label ?? ''}
                onChange={(e) =>
                  setValues((v) => ({ ...v, unit_label: e.target.value }))
                }
                placeholder="e.g., panel, outlet, breaker"
              />
              <p className="text-xs text-muted-foreground">
                Shown next to the quantity input on projects (e.g., "3 panels").
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <Label htmlFor="adder-active" className="cursor-pointer">
                Active
              </Label>
              <p className="text-xs text-muted-foreground">
                Inactive adders are hidden from the project picker.
              </p>
            </div>
            <Switch
              id="adder-active"
              checked={values.is_active}
              onCheckedChange={(checked) =>
                setValues((v) => ({ ...v, is_active: checked }))
              }
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : adder ? (
                'Save changes'
              ) : (
                'Add adder'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
